import React, { useState, useEffect } from 'react';
import { io } from 'socket.io-client';
import logger from '../../Entities/LoggerService';
import { Clock, Wifi, WifiOff, User, Timer, ChevronDown, ChevronUp } from 'lucide-react';
import { AuthService } from '../../Entities/AuthService';
import { getSocketConfig } from '../../utils/urlUtils';

const RealTimeStatus = () => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [sessionStart, setSessionStart] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [sessionDuration, setSessionDuration] = useState('00:00:00');
  const [lastLogin, setLastLogin] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Conectar Socket.IO
  useEffect(() => {
    // ✅ Usuário logado = sempre online (não depende de Socket.IO)
    const user = AuthService.getCurrentUser();
    if (user) {
      setIsConnected(true); // Marcar como conectado se usuário está logado
    }

    // ✅ Em produção: usa mesma URL do frontend (Nginx faz proxy)
    // ✅ Em desenvolvimento: usa porta 3010 diretamente
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const serverUrl = isDevelopment 
      ? `${window.location.protocol}//${window.location.hostname}:3010`
      : window.location.origin; // Usa mesma origem (Nginx faz proxy)
    
    const newSocket = io(serverUrl, {
      autoConnect: true,
      path: '/socket.io', // ✅ Caminho do Socket.IO (Nginx faz proxy)
      reconnection: true,
      reconnectionAttempts: 10,      // ⚡ Mais tentativas
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,                // ⚡ Timeout de 20s
      transports: ['websocket', 'polling'] // ⚡ Tentar WebSocket primeiro
    });

    newSocket.on('connect', () => {
      logger.info('realtime', 'RealTimeStatus.socketConnect', 'Conectado ao servidor em tempo real', {
        socketId: newSocket.id
      });
      setIsConnected(true);
      setLastUpdate(new Date());
    });

    newSocket.on('disconnect', () => {
      logger.warn('realtime', 'RealTimeStatus.socketDisconnect', 'Desconectado do servidor');
      // ✅ Não marcar como offline se usuário ainda está logado
      const currentUser = AuthService.getCurrentUser();
      if (!currentUser) {
        setIsConnected(false);
      }
    });

    newSocket.on('server_status', (data) => {
      setLastUpdate(new Date(data.timestamp));
    });

    newSocket.on('data_updated', (data) => {
      setLastUpdate(new Date());
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, []);

  // Atualizar tempo atual a cada segundo
  useEffect(() => {
    if (!sessionStart) return; // Aguardar sessionStart ser carregado
    
    const timer = setInterval(() => {
      setCurrentTime(new Date());
      
      // Calcular duração da sessão
      const now = new Date();
      const diff = now - sessionStart;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setSessionDuration(
        `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      );
    }, 1000);

    return () => clearInterval(timer);
  }, [sessionStart]);

  // Carregar início da sessão e último login do usuário
  useEffect(() => {
    const user = AuthService.getCurrentUser();
    if (user) {
      // Tentar obter início da sessão do localStorage
      let sessionStartTime = localStorage.getItem('sessionStartTime');
      
      if (!sessionStartTime) {
        // Se não existe no localStorage, usar last_login do usuário
        if (user.last_login) {
          sessionStartTime = new Date(user.last_login).getTime();
        } else {
          // Fallback: usar timestamp atual
          sessionStartTime = Date.now();
        }
        // Salvar no localStorage para persistir entre navegações
        localStorage.setItem('sessionStartTime', sessionStartTime);
      }
      
      setSessionStart(new Date(parseInt(sessionStartTime)));
      
      // Definir último login para exibição
      if (user.last_login) {
        setLastLogin(new Date(user.last_login));
      }
    }
  }, []);

  // Enviar atualização de sessão periodicamente
  useEffect(() => {
    if (socket && isConnected) {
      const sessionTimer = setInterval(() => {
        const user = AuthService.getCurrentUser();
        if (user) {
          socket.emit('update_session_time', {
            user: user.name,
            sessionDuration: sessionDuration
          });
        }
      }, 30000); // A cada 30 segundos

      return () => clearInterval(sessionTimer);
    }
  }, [socket, isConnected, sessionDuration]);

  const formatDateTime = (date) => {
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (date) => {
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white border rounded-lg shadow-sm transition-all duration-300 ease-in-out">
      {/* Header sempre visível - Otimizado para mobile */}
      <div 
        className="flex items-center justify-between p-3 sm:p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <h3 className="text-xs sm:text-sm font-medium text-gray-700 flex items-center truncate">
            <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
            <span className="hidden sm:inline">Status da Sessão</span>
            <span className="sm:hidden">Status</span>
          </h3>
          <div className="flex items-center">
            {isConnected ? (
              <div className="flex items-center text-green-600">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse mr-1"></div>
                <span className="text-xs font-medium">Online</span>
              </div>
            ) : (
              <div className="flex items-center text-red-600">
                <div className="w-2 h-2 rounded-full bg-red-500 mr-1"></div>
                <span className="text-xs font-medium">Offline</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Seta para expandir/recolher */}
        <button className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 ml-2">
          {isExpanded ? (
            <ChevronUp className="w-3 h-3 sm:w-4 sm:h-4" />
          ) : (
            <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4" />
          )}
        </button>
      </div>

      {/* Tempo Atual - sempre visível - Otimizado para mobile */}
      <div className="px-3 sm:px-4 pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0">
          <span className="text-xs sm:text-sm text-gray-600 flex items-center">
            <Clock className="w-3 h-3 mr-1 flex-shrink-0" />
            Agora:
          </span>
          <span className="font-mono text-xs sm:text-sm text-blue-600 font-medium break-all sm:break-normal">
            {formatDateTime(currentTime)}
          </span>
        </div>
      </div>

      {/* Conteúdo expansível - Otimizado para mobile */}
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
        isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="px-3 sm:px-4 pb-3 sm:pb-4 space-y-2 sm:space-y-3 border-t border-gray-100 pt-3">

        {/* Última Atualização */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0">
          <span className="text-xs sm:text-sm text-gray-600">Última Modificação:</span>
          <span className="font-mono text-xs sm:text-sm text-green-600 break-all sm:break-normal">
            {formatDate(lastUpdate)}
          </span>
        </div>

        {/* Tempo de Sessão */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0">
          <span className="text-xs sm:text-sm text-gray-600 flex items-center">
            <Timer className="w-3 h-3 mr-1 flex-shrink-0" />
            Sessão ativa:
          </span>
          <span className="font-mono text-xs sm:text-sm text-purple-600 font-semibold">
            {sessionDuration}
          </span>
        </div>

        {/* Último Login */}
        {lastLogin && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0">
            <span className="text-xs sm:text-sm text-gray-600 flex items-center">
              <User className="w-3 h-3 mr-1 flex-shrink-0" />
              Último login:
            </span>
            <span className="font-mono text-xs sm:text-sm text-gray-500 break-all sm:break-normal">
              {formatDate(lastLogin)}
            </span>
          </div>
        )}

        {/* Indicador de Conexão */}
        <div className="pt-2 border-t">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Atualizações automáticas:</span>
            <div className={`flex items-center ${isConnected ? 'text-green-600' : 'text-red-600'}`}>
              <div className={`w-2 h-2 rounded-full mr-1 ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
              <span className="font-medium">{isConnected ? 'Ativas' : 'Inativas'}</span>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default RealTimeStatus;
